import React from 'react';
import { Box, Button, Stack } from '@mui/material';
import { getFileIconSrc } from '@/components/AttachmentFile/utils';
import { CircleDeleteIcon } from '@/theme/overrides/CustomIcons';
import Spinner from '@/components/@IntranetLibrary/Spinner';

export type LegacyFileCardStatus = 'success' | 'loading' | 'error';

export type LegacyFileCardProps = {
  readonly name: string;
  readonly link?: string;
  readonly status?: LegacyFileCardStatus;
  readonly onRemove?: () => void;
};

/**
 * 품의(전자결재) 코멘트 전용 비이미지 첨부 카드 (레거시 디자인).
 * - 좌측 파일 타입 아이콘(getFileIconSrc — 신규 mime 리스트 X), 우측 파란 underline 파일명.
 * - 클릭 = 새창(window.open, noopener). 다운로드 기능/attr 없음 (사용자 명시).
 * - status='loading' 스피너 + 클릭 비활성, 'error' 회색.
 */
const LegacyFileCard = ({
  name,
  link,
  status = 'success',
  onRemove,
}: LegacyFileCardProps) => {
  const isLoading = status === 'loading';
  const isError = status === 'error';
  const iconSrcName = getFileIconSrc(name);

  const openInNewWindow = () => {
    if (isLoading || isError || !link) return;
    window.open(link, '_blank', 'noopener');
  };

  return (
    <Stack
      direction="row"
      alignItems="center"
      sx={{
        display: 'inline-flex',
        gap: '6px',
        maxWidth: '100%',
        opacity: isLoading ? 0.5 : 1,
      }}
    >
      <img
        srcSet={`${iconSrcName}.png, ${iconSrcName}@2x.png 2x, ${iconSrcName}@3x.png 3x`}
        src={`${iconSrcName}.png`}
        alt={`${name} icon`}
        style={{ width: '24px', height: '24px', flexShrink: 0 }}
        className="no-drag"
      />
      <Box
        component="span"
        onClick={openInNewWindow}
        title={name}
        className="ellipsis no-drag"
        sx={{
          color: isError ? '#A0A4A8' : '#0066cc',
          textDecoration: 'underline',
          cursor: isLoading || isError || !link ? 'default' : 'pointer',
          overflow: 'hidden',
          whiteSpace: 'nowrap',
          textOverflow: 'ellipsis',
          minWidth: 0,
          flexShrink: 1,
        }}
      >
        {name}
      </Box>
      {isLoading && (
        <Box sx={{ flexShrink: 0, display: 'flex', alignItems: 'center' }}>
          <Spinner theme="black" />
        </Box>
      )}
      {onRemove && !isLoading && (
        <Button
          disableRipple
          onMouseDown={(e) => {
            e.preventDefault();
            e.stopPropagation();
          }}
          onClick={(e) => {
            e.preventDefault();
            e.stopPropagation();
            onRemove();
          }}
          sx={{
            minWidth: 'auto',
            width: '20px',
            height: '20px',
            padding: 0,
            flexShrink: 0,
          }}
        >
          <CircleDeleteIcon />
        </Button>
      )}
    </Stack>
  );
};

export default LegacyFileCard;
